import { ChevronDown } from 'lucide-react'
import { TrendDirectionSummary } from './TrendDirectionSummary'
import { QuestionTrendsChart } from './QuestionTrendsChart'
import { ReportVolumeTimeline } from './ReportVolumeTimeline'
import { GemsPerStreamChart } from './GemsPerStreamChart'
import { KeyMetricsCards } from './KeyMetricsCards'
import type { TrendsViewProps } from './types'

const DATE_RANGE_OPTIONS = [
  { label: 'Past 30 days', value: '30' },
  { label: 'Past 90 days', value: '90' },
  { label: 'Past year', value: '365' },
  { label: 'All time', value: 'all' },
]

export function TrendsView({
  streams,
  selectedStreamId,
  selectedDays,
  trendDirectionSummary,
  questionTrendsOverTime,
  reportVolumeTimeline,
  gemsPerStream,
  keyMetrics,
  onStreamChange,
  onDaysChange,
}: TrendsViewProps) {
  const selectedStream = streams.find(s => s.id === selectedStreamId)
  const selectedDateRange = DATE_RANGE_OPTIONS.find(d => d.value === selectedDays)

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                Trends
              </h1>
              <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                {selectedStream ? `Analyzing ${selectedStream.name}` : 'Account-wide analytics'}
                {selectedDateRange && ` · ${selectedDateRange.label}`}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Stream Selector */}
              <div className="flex-1 min-w-[140px] max-w-[180px]">
                <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                  Stream
                </label>
                <div className="relative">
                  <select
                    value={selectedStreamId || ''}
                    onChange={(e) => onStreamChange?.(e.target.value || null)}
                    className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  >
                    <option value="">All Streams</option>
                    {streams.map((stream) => (
                      <option key={stream.id} value={stream.id}>
                        {stream.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                </div>
              </div>

              {/* Date Range Selector */}
              <div className="flex-1 min-w-[130px] max-w-[160px]">
                <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                  Time Period
                </label>
                <div className="relative">
                  <select
                    value={selectedDays}
                    onChange={(e) => onDaysChange?.(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  >
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        {/* Key Metrics Cards */}
        <KeyMetricsCards metrics={keyMetrics} />

        {/* Trend Direction Summary */}
        <TrendDirectionSummary data={trendDirectionSummary} />

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Question Trends Over Time */}
          <QuestionTrendsChart data={questionTrendsOverTime} />

          {/* Report Volume Timeline */}
          <ReportVolumeTimeline data={reportVolumeTimeline} />
        </div>

        {/* Gems Per Stream */}
        <GemsPerStreamChart data={gemsPerStream} />
      </main>
    </div>
  )
}
